// stateGroups.js — SINGLE SOURCE OF TRUTH for all state classification in PilotLinkUSA.
// Consumed by TrainingHub.jsx (Certification Map), PPCAAllStatesCrashCourse.jsx
// (Crash Course groups), and any backend/dispatcher view needing state metadata.
//
// Three independent, non-contradictory classification axes per state:
//   legal_requirement_group  — what the law requires (FHWA + state DOT)
//   reciprocity_group        — where the certificate is recognized
//   training_group           — how PilotLinkUSA teaches/organizes the state
//
// Regenerate this file from unified_state_schema.json if group membership changes;
// do not hand-edit individual group arrays elsewhere in the app.

export const MASTER_STATES = [
  {
    code: "AK", name: "Alaska",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "AL", name: "Alabama",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "pevo14",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "AR", name: "Arkansas",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "pevo14",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "AZ", name: "Arizona",
    legal_requirement_group: "pevo_required",
    reciprocity_group: "network",
    training_group: "eight_state",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "CA", name: "California",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "standalone",
    notes: "No PEVO operator certification required by law, but a dense CHP/Caltrans permit system (weekend/holiday travel bans, tiered escort triggers) applies instead.",
    override_rules: { requiresAdditionalLocalPermit: true, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "CO", name: "Colorado",
    legal_requirement_group: "pevo_required",
    reciprocity_group: "network",
    training_group: "pevo14",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "CT", name: "Connecticut",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "pevo14",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "DC", name: "Washington DC",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "DE", name: "Delaware",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "pevo14",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "FL", name: "Florida",
    legal_requirement_group: "pevo_required",
    reciprocity_group: "network",
    training_group: "pevo14",
    notes: "Accepts a national PEVO cert but additionally requires a defensive-driving certificate before escort operations.",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: true },
  },
  {
    code: "GA", name: "Georgia",
    legal_requirement_group: "pevo_required",
    reciprocity_group: "network",
    training_group: "pevo14",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "HI", name: "Hawaii",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "IA", name: "Iowa",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "eight_state",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "ID", name: "Idaho",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "eight_state",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "IL", name: "Illinois",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "IN", name: "Indiana",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "KS", name: "Kansas",
    legal_requirement_group: "pevo_required",
    reciprocity_group: "network",
    training_group: "eight_state",
    notes: "Requires a recognized pilot/escort certification — no full state-run program of its own, accepts certifications from other network states. Also requires pre-registration with KDOT and additional paperwork per move, separate from the certification itself.",
    override_rules: { requiresAdditionalLocalPermit: true, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "KY", name: "Kentucky",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "pevo14",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "LA", name: "Louisiana",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "pevo14",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "MA", name: "Massachusetts",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "MD", name: "Maryland",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "ME", name: "Maine",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "MI", name: "Michigan",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "pevo14",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "MN", name: "Minnesota",
    legal_requirement_group: "pevo_required",
    reciprocity_group: "network",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "MO", name: "Missouri",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "eight_state",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "MS", name: "Mississippi",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "pevo14",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "MT", name: "Montana",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "NC", name: "North Carolina",
    legal_requirement_group: "pevo_required",
    reciprocity_group: "network",
    training_group: "pevo14",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "ND", name: "North Dakota",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "NE", name: "Nebraska",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "eight_state",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "NH", name: "New Hampshire",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "NJ", name: "New Jersey",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "NM", name: "New Mexico",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "pevo14",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "NV", name: "Nevada",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "eight_state",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "NY", name: "New York",
    legal_requirement_group: "independent_required",
    reciprocity_group: "independent",
    training_group: "remaining",
    notes: "Zero reciprocity — does not accept any other state's certification, and no other state accepts New York's. Requires the MV-64 Certified Vehicle Escort Card, issued and tested by the NY Department of Motor Vehicles (NY DMV) — not NYSDOT.",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "OH", name: "Ohio",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "OK", name: "Oklahoma",
    legal_requirement_group: "pevo_required",
    reciprocity_group: "network",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "OR", name: "Oregon",
    legal_requirement_group: "independent_required",
    reciprocity_group: "independent",
    training_group: "standalone",
    notes: "⚠️ UNVERIFIED — flagged for review. ODOT's own site and a national FHWA best-practices survey describe Oregon as requiring certified FLAGGERS for specific routes/situations, not a blanket pilot-car-operator certification — Oregon isn't listed among the states with a formal P/EVO certification program. This entry's 'independent_required' classification may be incorrect. Confirm current ODOT policy directly before treating this as ground truth.",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "PA", name: "Pennsylvania",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "network",
    training_group: "remaining",
    notes: "Accepts the network cert, but only for Superloads, plus a defensive-driving/flagger course, and does not accept certifications the other direction — one-directional exception, so excluded from the Legal 'pevo_required' 11 despite being Reciprocity:network.",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: true, additionalCourseRequired: false },
  },
  {
    code: "RI", name: "Rhode Island",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "SC", name: "South Carolina",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "pevo14",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "SD", name: "South Dakota",
    legal_requirement_group: "independent_required",
    reciprocity_group: "independent",
    training_group: "remaining",
    notes: "Requires its own state-specific certification — not part of the reciprocity network. Confirm current requirements with SDDOT.",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "TN", name: "Tennessee",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "standalone",
    notes: "No separate state-specific escort certification beyond a valid national PEVO certificate; TDOT issues the load's transportation permit directly.",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "TX", name: "Texas",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "network",
    training_group: "standalone",
    notes: "TxDMV confirms Texas has no state-issued escort license — any training accepted by another state for an escort flagger or pilot/escort vehicle operator is also accepted in Texas. Training is only required for the flagger who directs traffic, not for every escort driver.",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "UT", name: "Utah",
    legal_requirement_group: "pevo_required",
    reciprocity_group: "network",
    training_group: "eight_state",
    notes: "Full reciprocity both directions; also requires a separate permit for the escort vehicle itself.",
    override_rules: { requiresAdditionalLocalPermit: true, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "VA", name: "Virginia",
    legal_requirement_group: "pevo_required",
    reciprocity_group: "network",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "VT", name: "Vermont",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "WA", name: "Washington",
    legal_requirement_group: "pevo_required",
    reciprocity_group: "network",
    training_group: "standalone",
    notes: "Requires a valid pilot/escort certification per WAC 468-38-100 — either a Washington card, or a certification from a jurisdiction WSDOT has approved for reciprocity. Not an extra permit stacked on top of PEVO; the reciprocal cert IS what satisfies the requirement. Confirm your state is currently on WSDOT's approved list before entering WA, since the list is reviewed periodically.",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "WI", name: "Wisconsin",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "standalone",
    notes: "No certification required. Confirm current escort trigger dimensions with WisDOT.",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "WV", name: "West Virginia",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "remaining",
    notes: "",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
  {
    code: "WY", name: "Wyoming",
    legal_requirement_group: "no_state_requirement",
    reciprocity_group: "no_cert",
    training_group: "standalone",
    notes: "No certification required.",
    override_rules: { requiresAdditionalLocalPermit: false, oneDirectionalRecognitionOnly: false, additionalCourseRequired: false },
  },
];

// ── Derived group arrays (do not maintain these by hand — they are always
//    filtered from MASTER_STATES so the three systems can never drift apart) ──

export const LEGAL_GROUPS = {
  pevoRequired: MASTER_STATES.filter(s => s.legal_requirement_group === "pevo_required").map(s => s.code),
  independentRequired: MASTER_STATES.filter(s => s.legal_requirement_group === "independent_required").map(s => s.code),
  noStateRequirement: MASTER_STATES.filter(s => s.legal_requirement_group === "no_state_requirement").map(s => s.code),
};

export const RECIPROCITY_GROUPS = {
  network: MASTER_STATES.filter(s => s.reciprocity_group === "network").map(s => s.code),
  independent: MASTER_STATES.filter(s => s.reciprocity_group === "independent").map(s => s.code),
  noCert: MASTER_STATES.filter(s => s.reciprocity_group === "no_cert").map(s => s.code),
};

export const TRAINING_GROUPS = {
  pevo14: MASTER_STATES.filter(s => s.training_group === "pevo14").map(s => s.code),
  eightState: MASTER_STATES.filter(s => s.training_group === "eight_state").map(s => s.code),
  standalone: MASTER_STATES.filter(s => s.training_group === "standalone").map(s => s.code),
  remaining: MASTER_STATES.filter(s => s.training_group === "remaining").map(s => s.code),
};

export function findStateMeta(code) {
  return MASTER_STATES.find(s => s.code === code) || null;
}

// Back-compat shim for existing TrainingHub.jsx map-flavor logic, which
// previously read ad-hoc `inReciprocityNetwork` / `independentCert` / `noCertRequired`
// booleans computed by hand per state. Now always derived from reciprocity_group.
export function getStateFlags(code) {
  const s = findStateMeta(code);
  if (!s) return { inReciprocityNetwork: false, independentCert: false, noCertRequired: false };
  return {
    inReciprocityNetwork: s.reciprocity_group === "network",
    independentCert: s.reciprocity_group === "independent",
    noCertRequired: s.reciprocity_group === "no_cert",
  };
}